import React, { ComponentProps, useMemo } from 'react'
import View, { Carousel } from './AssetDisplay.view'
import { AssetDisplayMode, AssetDisplayProps } from './AssetDisplay.types'
import { AssetType } from 'core/logic/asset/asset.types'

export function useAssetMedias(
  asset: AssetDisplayProps['asset'],
  _uniqueId: AssetDisplayProps['uniqueId']
) {
  const mainMediaConfig = useMemo<ComponentProps<typeof View>>(() => {
    const uniqueId = _uniqueId || String(asset.tokenId) + String(asset.id)
    const medias = asset.media
    if (AssetDisplayMode.IMAGE in medias)
      return {
        mode: AssetDisplayMode.IMAGE,
        src: medias[AssetDisplayMode.IMAGE],
        uniqueId,
      }
    if (AssetDisplayMode.ANIMATION in medias)
      return {
        mode: AssetDisplayMode.ANIMATION,
        src: medias[AssetDisplayMode.ANIMATION],
        uniqueId,
      }
    if (AssetDisplayMode.VIDEO in medias)
      return {
        mode: AssetDisplayMode.VIDEO,
        src: medias[AssetDisplayMode.VIDEO],
        thumbnail: medias.coverUrl,
      }

    const mediasDeprec = medias as any
    /** @deprecated Modes that have been since deprecated, kept for retrocomptibility */
    if ('animationUrl' in mediasDeprec) {
      return {
        mode: AssetDisplayMode.ANIMATION,
        src: mediasDeprec.animationUrl!,
        uniqueId,
      }
    }
    if ('imageUrl' in mediasDeprec || 'coverUrl' in mediasDeprec)
      return {
        mode: AssetDisplayMode.IMAGE,
        src: mediasDeprec.imageUrl || mediasDeprec.coverUrl!,
        uniqueId,
      }
    console.error(`Something has gone very wrong, there is no animation or image for this asset
      
Asset.medias: ${JSON.stringify(mediasDeprec)}`)
    return { mode: AssetDisplayMode.IMAGE, src: '', uniqueId }
  }, [asset])
  const extraMedias = useMemo<ComponentProps<typeof Carousel>['extra']>(() => {
    const imagesToConsider = asset.saleImages
      ? asset.saleImages
      : asset.media.additionalAssets

    if (!imagesToConsider) return []
    return imagesToConsider
      .map((a, idx) => {
        const x = {
          mode: AssetDisplayMode.IMAGE,
          src: a,
          uniqueId:
            'uniqueId' in mainMediaConfig
              ? `${mainMediaConfig.uniqueId}-${idx}`
              : '',
          customProportion: imagesToConsider === asset.saleImages,
        } as const
        return x
      })
      .filter(Boolean as unknown as BooleanAsFilter)
  }, [asset])

  return {
    mainMediaConfig,
    extraMedias,
    mediaConfigs: [mainMediaConfig, ...extraMedias],
  }
}

export default function AssetDisplayLogic({
  asset,
  uniqueId: _uniqueId,
  zoomable,
  extraMedias: displayExtraMedias = false,
}: AssetDisplayProps) {
  const { extraMedias, mainMediaConfig } = useAssetMedias(asset, _uniqueId)
  if (displayExtraMedias && extraMedias.length)
    return <Carousel main={mainMediaConfig} extra={extraMedias} />
  return <View {...mainMediaConfig} zoomable={zoomable} />
}
